// Phase 5.3 — Interface Language context. Account-level, INDEPENDENT of Learning Profile.
// Resolution: account preference (after login) > stored local preference > browser > English.
// Never silently overrides an authenticated user's chosen locale with the browser language.
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { translate, localeDir, DICTS, DEFAULT_LOCALE } from "@/lib/i18n";

const I18nContext = createContext(null);
const STORAGE_KEY = "fluent_interface_locale";

function initialLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && DICTS[stored]) return stored;
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  return DICTS[nav] ? nav : DEFAULT_LOCALE;
}

function applyDocument(locale) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  document.documentElement.dir = localeDir(locale);
}

export function I18nProvider({ children }) {
  const { user, setUser } = useAuth();
  const [locale, setLocaleState] = useState(initialLocale);
  const appliedAccount = useRef(false);

  // Apply dir/lang to <html> on every locale change.
  useEffect(() => { applyDocument(locale); }, [locale]);

  // When the authenticated user loads, their saved account preference takes priority
  // (applied once, so a later manual switch isn't overwritten).
  useEffect(() => {
    if (user?.interface_locale && DICTS[user.interface_locale] && !appliedAccount.current) {
      appliedAccount.current = true;
      if (user.interface_locale !== locale) {
        setLocaleState(user.interface_locale);
        localStorage.setItem(STORAGE_KEY, user.interface_locale);
      }
    }
    if (!user) appliedAccount.current = false;
  }, [user, locale]);

  const setLocale = useCallback(async (next) => {
    if (!DICTS[next]) return;
    setLocaleState(next);
    localStorage.setItem(STORAGE_KEY, next);
    applyDocument(next);
    // Persist to the account (best-effort) when authenticated — never blocks the UI.
    if (user) {
      try {
        const r = await api.patch("/auth/interface-locale", { locale: next });
        if (r?.data) setUser(r.data);
      } catch { /* non-blocking; local preference still applies */ }
    }
  }, [user, setUser]);

  const t = useCallback((key, vars) => translate(locale, key, vars), [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, dir: localeDir(locale), isRtl: localeDir(locale) === "rtl" }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext) || {
  // Safe fallback if a component renders outside the provider (keeps UI usable).
  locale: DEFAULT_LOCALE, setLocale: () => {}, dir: "ltr", isRtl: false,
  t: (k, v) => translate(DEFAULT_LOCALE, k, v),
};
